import SELECTORS from "@ui/config/class-selectors.config";

const CONTAINER_MAPPINGS = [
  { selector: SELECTORS.HomepageHero, name: "homepage-hero" },
  { selector: SELECTORS.FourGridContainer, name: "four-grid-container" },
  { selector: SELECTORS.RiverContainer, name: "river-container" },
  { selector: SELECTORS.CategorySpotlight, name: "category-spotlight" },
  { selector: SELECTORS.VariableStorySet, name: "variable-story-set" },
  { selector: SELECTORS.MostPopular, name: "most-popular" },
  { selector: SELECTORS.MostDiscussed, name: "most-discussed" },
  { selector: SELECTORS.CategoryShelf, name: "category-shelf" },
];

/**
 * Calculates the 1-based position of a content card in the DOM relative to other visible content cards.
 * @param element The element that triggered the event (usually the click target or current target).
 * @returns The 1-based position within its container.
 */
export const getContentCardPosition = (
  element: HTMLElement | EventTarget | null,
): string | undefined => {
  try {
    if (!element || !(element instanceof HTMLElement)) {
      return undefined;
    }

    const cardSelector = `.${SELECTORS.ContentCard}`;
    const card = element.closest(cardSelector);
    if (!card) {
      return undefined;
    }

    // Check if the card is inside one of the special containers
    for (const { selector, name } of CONTAINER_MAPPINGS) {
      const containerSelector = `.${selector}`;
      const container = card.closest(containerSelector);

      if (!container) {
        continue;
      }

      // Find all visible instances of this container type
      const allContainers = Array.from(
        document.querySelectorAll(containerSelector),
      ).filter((c) => c instanceof HTMLElement && c.offsetParent !== null);
      const containerIndex = allContainers.indexOf(container);
      // If the container itself is not visible (not in the filtered list), skip it

      if (containerIndex === -1) {
        continue;
      }

      // Find cards within THIS container
      const cardsInContainer = Array.from(
        container.querySelectorAll(cardSelector),
      ).filter((c) => c instanceof HTMLElement && c.offsetParent !== null);
      const cardIndex = cardsInContainer.indexOf(card as HTMLElement);

      if (cardIndex === -1) {
        continue;
      }

      // If there are multiple containers, suffix the name
      const componentName =
        allContainers.length > 1 ? `${name}-${containerIndex + 1}` : name;

      return `${componentName}:card-${cardIndex + 1}`;
    }

    return undefined;
  } catch {
    return undefined;
  }
};
